import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  Box,
  Button,
  ButtonMode,
  Dialog,
  NotificationErrorIcon
} from 'edenred-ui';
import { useDeleteBenefitGroupMutation } from '@epi-api/benefitGroups/benefitGroupsApi';
import { useDispatch } from 'react-redux';

import { showNotification } from '@epi-actions/overlays';
import { routerPaths } from '@epi-constants/routerPaths';
import { goToPage } from '@epi-actions/navigation';
import {
  DeleteGroupModalContentType,
  DeleteGroupModalProps
} from '@epi-models/pages/BenefitGroups';

export const DeleteGroupModal = ({
  show,
  onClose,
  groupId,
  groupName,
  beneficiaryCount,
  isLastGroup,
  isProcessing
}: DeleteGroupModalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { sizeExtraExtraLarge, gray85, fontSemiBold, sizeMedium, gray50 } =
    useTheme();
  const [deleteBenefitGroup] = useDeleteBenefitGroupMutation();
  const getModalContentType = (): DeleteGroupModalContentType => {
    if (beneficiaryCount > 0) return 'not_empty';
    if (isLastGroup) return 'last_group';
    if (isProcessing) return 'in_progress';
    return 'confirm';
  };
  const modalContentType = getModalContentType();

  const handleDelete = async () => {
    try {
      await deleteBenefitGroup({ groupId: groupId.toString() }).unwrap();
      dispatch(
        showNotification(
          { message: 'containers.benefit_groups.delete_group_success' },
          'success'
        )
      );
    } catch (error) {
      dispatch(showNotification({ message: 'messages.error' }, 'error'));
    }
    onClose();
  };

  const goToManageBeneficiaries = () => {
    dispatch(goToPage(`${routerPaths.beneficiaries}?groupId=${groupId}`));
  };

  const getActionButtons = () => {
    return (
      <>
        <Button
          id="cancel-delete-benefit-group"
          onClick={onClose}
          mode={ButtonMode.Secondary}
          minWidth={135}
        >
          {modalContentType === 'confirm'
            ? t('containers.benefit_groups.delete_group_modal.cancel')
            : t('containers.benefit_groups.delete_group_modal.close')}
        </Button>
        {modalContentType === 'not_empty' && (
          <Button
            id="manage-employees"
            mode={ButtonMode.Secondary}
            minWidth={135}
            type="submit"
            onClick={goToManageBeneficiaries}
          >
            {t('containers.benefit_groups.delete_group_modal.manage_employees')}
          </Button>
        )}
        {modalContentType === 'confirm' && (
          <Button
            id="delete-benefit-group"
            mode={ButtonMode.Primary}
            minWidth={135}
            type="submit"
            onClick={handleDelete}
          >
            {t('containers.benefit_groups.delete_group_modal.submit')}
          </Button>
        )}
      </>
    );
  };

  return (
    <Dialog open={show} onClose={onClose} maxWidth="md">
      <Dialog.Content>
        <Box display="flex" gap={2} alignItems="flex-start">
          {modalContentType === 'confirm' ? null : (
            <Box display="flex" alignItems="center" gap={2}>
              <NotificationErrorIcon height={32} />
            </Box>
          )}
          <Box>
            <Box
              fontSize={sizeExtraExtraLarge}
              fontWeight={fontSemiBold}
              color={gray85}
            >
              {t(
                `containers.benefit_groups.delete_group_modal.${modalContentType}.title`,
                {
                  groupName
                }
              )}
            </Box>
            <Box fontSize={sizeMedium} color={gray50}>
              {t(
                `containers.benefit_groups.delete_group_modal.${modalContentType}.content`,
                {
                  groupName
                }
              )}
            </Box>
          </Box>
        </Box>
      </Dialog.Content>
      <Dialog.Actions>
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="flex-end"
        >
          <Box display="flex" gap={2}>
            {getActionButtons()}
          </Box>
        </Box>
      </Dialog.Actions>
    </Dialog>
  );
};
